import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  SimpleButton,
  LoadingButton,
  Input,
  TextArea,
  Select,
  FormDialog,
  Toast,
  NotificationsList,
  Spinner,
  useStateContext,
} from "@veda/dashboard-ui";
import { BsArrowLeft, BsFilePdf, BsBoxArrowUpRight } from "react-icons/bs";
import { usePppApi } from "../services/pppApi";
import { useAuthUser } from "../hooks/useAuthUser";
import { usePppAiContext } from "../hooks/usePppAiContext";

const STATO_CFG = {
  bozza:       { key: "ppp_dip_stato_bozza",       cls: "bg-gray-100 text-gray-700" },
  in_revisione:{ key: "ppp_dip_stato_in_revisione", cls: "bg-yellow-100 text-yellow-800" },
  approvato:   { key: "ppp_dip_stato_approvato",    cls: "bg-green-100 text-green-700" },
};

const STATO_PROPOSTA_CFG = {
  ricevuta:      { cls: "bg-blue-100 text-blue-700" },
  in_valutazione:{ cls: "bg-yellow-100 text-yellow-800" },
  ammessa:       { cls: "bg-green-100 text-green-700" },
  respinta:      { cls: "bg-red-100 text-red-700" },
  ritirata:      { cls: "bg-gray-100 text-gray-500" },
};

function scadenzaInfo(dataDecisioneAvvio, dataTermineRedazione) {
  if (!dataDecisioneAvvio) return null;
  const scadenza = dataTermineRedazione
    ? new Date(dataTermineRedazione)
    : new Date(new Date(dataDecisioneAvvio).getTime() + 30 * 86400000);
  const oggi = new Date();
  oggi.setHours(0, 0, 0, 0);
  const giorni = Math.round((scadenza - oggi) / 86400000);
  if (giorni < 0)  return { giorni, cls: "bg-red-50 border-red-300 text-red-700",    icon: "⚠", label: `Scaduta da ${Math.abs(giorni)} giorni` };
  if (giorni <= 7) return { giorni, cls: "bg-red-50 border-red-300 text-red-700",    icon: "⚠", label: `Scade tra ${giorni} giorni` };
  if (giorni <= 15)return { giorni, cls: "bg-amber-50 border-amber-300 text-amber-700", icon: "⏳", label: `Scade tra ${giorni} giorni` };
  return               { giorni, cls: "bg-blue-50 border-blue-200 text-blue-700",   icon: "📅", label: `${giorni} giorni alla scadenza` };
}

function StatoBadge({ stato, _locate }) {
  const cfg = STATO_CFG[stato] ?? { key: stato, cls: "" };
  return (
    <span className={`px-2 py-0.5 rounded text-sm font-medium ${cfg.cls}`}>
      {_locate(cfg.key) ?? stato}
    </span>
  );
}

function formatEuro(val) {
  if (!val) return "—";
  return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Number(val));
}

function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("it-IT");
}

function InfoRow({ label, value }) {
  return (
    <div className="flex flex-col">
      <span className="text-xs text-gray-500 uppercase tracking-wide">{label}</span>
      <span className="text-sm text-gray-900 dark:text-white mt-0.5 whitespace-pre-wrap">{value || "—"}</span>
    </div>
  );
}

const ESITO_CFG = {
  conforme:         { cls: "bg-green-100 text-green-700",   label: "Conforme" },
  parziale:         { cls: "bg-yellow-100 text-yellow-800", label: "Parziale" },
  non_conforme:     { cls: "bg-red-100 text-red-700",       label: "Non conforme" },
  non_verificabile: { cls: "bg-gray-100 text-gray-600",     label: "Non verificabile" },
};

function EsitoBadge({ esito }) {
  const cfg = ESITO_CFG[esito] ?? ESITO_CFG.non_verificabile;
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${cfg.cls}`}>{cfg.label}</span>
  );
}

export default function DipDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { _locate, setOpenForm, addToast } = useStateContext();
  const { can } = useAuthUser();
  usePppAiContext('ppp.dip', { id });
  const api = usePppApi();

  const [dip, setDip]                   = useState(null);
  const [procedimenti, setProcedimenti] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [saving, setSaving]             = useState(false);
  const [showApprova, setShowApprova]   = useState(false);
  const [editErrors, setEditErrors]     = useState(null);
  const [approvaErrors, setApprovaErrors] = useState(null);

  const [coerenzaMap, setCoerenzaMap]         = useState({});
  const [coerenzaLoading, setCoerenzaLoading] = useState({});

  const [editForm, setEditForm] = useState(null);
  const [approvaForm, setApprovaForm] = useState({ numeroAttoApprovazione: "", dataApprovazione: "" });

  const loadDip = useCallback(() => {
    api.getDip(id, {
      onSuccess: (data) => { setDip(data); setLoading(false); },
      onError:   () => setLoading(false),
    });
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadDip();
    api.listProcedimenti({ onSuccess: (data) => setProcedimenti(data ?? []) });
  }, [loadDip]); // eslint-disable-line react-hooks/exhaustive-deps

  const openEdit = (d) => {
    setEditForm({
      oggetto:             d.oggetto ?? "",
      procedimentoId:      d.procedimentoId ?? "",
      statoDeiLuoghi:      d.statoDeiLuoghi ?? "",
      obiettiviFabbisogni: d.obiettiviFabbisogni ?? "",
      livelliServizio:     d.livelliServizio ?? "",
      requisitiTecnici:    d.requisitiTecnici ?? "",
      limiteEconomico:     d.limiteEconomico ?? "",
      coberturaFinanziaria:d.coberturaFinanziaria ?? "",
      haDocfap:            d.haDocfap ?? false,
      numeroDocfap:        d.numeroDocfap ?? "",
      dataDecisioneAvvio:  d.dataDecisioneAvvio ?? "",
      dataTermineRedazione:d.dataTermineRedazione ?? "",
      note:                d.note ?? "",
    });
    setOpenForm(true);
  };

  const setEdit = (field) => (e) => setEditForm((f) => ({ ...f, [field]: e.target.value }));
  const setEditCheck = (field) => (e) => setEditForm((f) => ({ ...f, [field]: e.target.checked }));

  const handleAggiorna = () => {
    if (!editForm?.oggetto) return;
    setSaving(true);
    setEditErrors(null);
    api.aggiornaDip(dip.id, {
      ...editForm,
      procedimentoId: editForm.procedimentoId ? parseInt(editForm.procedimentoId) : null,
      limiteEconomico: editForm.limiteEconomico ? parseFloat(editForm.limiteEconomico) : null,
    }, {
      onSuccess: () => {
        addToast(<Toast duration={4} content={_locate("ppp_dip_aggiornato")} />);
        setSaving(false);
        setOpenForm(false);
        setEditErrors(null);
        loadDip();
      },
      onError:     () => setSaving(false),
      onFormError: (response) => { setSaving(false); setEditErrors(response.errors); },
    });
  };

  const handleInviaInRevisione = () => {
    api.inviaInRevisioneDip(dip.id, {
      onSuccess: () => {
        addToast(<Toast duration={4} content={_locate("ppp_dip_inviato_revisione")} />);
        loadDip();
      },
      onFormError: (response) => {
        const msg = response?.errors?.campi?.[0] ?? response?.message ?? "Errore di validazione.";
        addToast(<Toast duration={8} type="error" content={msg} />);
      },
    });
  };

  const handleDownloadDelibera = async () => {
    const url = api.urlDeliberaDip(dip.id);
    const response = await fetch(url, {
      credentials: "include",
      headers: { Accept: "application/pdf", "X-Requested-With": "XMLHttpRequest" },
    });
    if (!response.ok) return;
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = `delibera_dip_${dip.id}.pdf`;
    a.click();
    URL.revokeObjectURL(blobUrl);
  };

  const handleApprova = () => {
    if (!approvaForm.numeroAttoApprovazione || !approvaForm.dataApprovazione) return;
    setSaving(true);
    setApprovaErrors(null);
    api.approvaDip(dip.id, approvaForm, {
      onSuccess: () => {
        addToast(<Toast duration={4} content={_locate("ppp_dip_approvato_ok")} />);
        setSaving(false);
        setShowApprova(false);
        setApprovaErrors(null);
        loadDip();
      },
      onError:     () => setSaving(false),
      onFormError: (response) => { setSaving(false); setApprovaErrors(response.errors); },
    });
  };

  const aggiornaCoerenza = (propostaId) => {
    setCoerenzaLoading((prev) => ({ ...prev, [propostaId]: true }));
    api.coerenzaProposta(dip.id, propostaId, {
      onSuccess: (data) => {
        setCoerenzaMap((prev) => ({ ...prev, [propostaId]: data }));
        setCoerenzaLoading((prev) => ({ ...prev, [propostaId]: false }));
      },
      onError: () => setCoerenzaLoading((prev) => ({ ...prev, [propostaId]: false })),
    });
  };

  if (loading) return <Spinner />;
  if (!dip)    return null;

  const isEditable    = dip.stato !== "approvato";
  const isBozza       = dip.stato === "bozza";
  const isInRevisione = dip.stato === "in_revisione";
  const scadenza      = dip.stato !== "approvato" ? scadenzaInfo(dip.dataDecisioneAvvio, dip.dataTermineRedazione) : null;

  return (
    <div className="p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={() => navigate("/ppp/dip")}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
          >
            <BsArrowLeft /> {_locate("ppp_dip_titolo_lista")}
          </button>
          {dip.procedimentoId && (
            <button
              onClick={() => navigate(`/ppp/${dip.procedimentoId}`)}
              className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
            >
              {dip.procedimentoTitolo ?? _locate("ppp_col_procedimento")} <BsBoxArrowUpRight size={12} />
            </button>
          )}
        </div>
        <div className="flex items-start justify-between gap-6">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white leading-tight mb-1">
              {dip.oggetto}
            </h1>
            <StatoBadge stato={dip.stato} _locate={_locate} />
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end shrink-0">
            {isBozza && can('ppp.dip.edit') && (
              <SimpleButton customFunc={handleInviaInRevisione} text={_locate("ppp_dip_invia_revisione")} />
            )}
            {isInRevisione && can('ppp.dip.approve') && (
              <SimpleButton customFunc={() => setShowApprova(true)} text={_locate("ppp_dip_approva")} />
            )}
            {dip.stato === "approvato" && (
              <button
                onClick={handleDownloadDelibera}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 transition-colors"
              >
                <BsFilePdf className="text-base" />
                {_locate("ppp_dip_scarica_delibera")}
              </button>
            )}
            {isEditable && (
              <SimpleButton customFunc={() => openEdit(dip)} text={_locate("ppp_modifica")} />
            )}
          </div>
        </div>
      </div>

      {/* Banner scadenza redazione */}
      {scadenza && (
        <div className={`border rounded-lg px-4 py-3 mb-4 flex items-center gap-3 ${scadenza.cls}`}>
          <span className="text-lg">{scadenza.icon}</span>
          <div>
            <span className="font-medium">{_locate("ppp_dip_scadenza_redazione")}: </span>
            <span>{scadenza.label}</span>
            {dip.dataTermineRedazione && (
              <span className="ml-2 text-sm opacity-80">({formatDate(dip.dataTermineRedazione)})</span>
            )}
          </div>
        </div>
      )}

      {/* Informazioni generali */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5 mb-4">
        <h2 className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-4">
          Informazioni generali
        </h2>
        <div className="grid grid-cols-2 gap-4">
          <InfoRow label={_locate("ppp_dip_procedimento")} value={dip.procedimentoTitolo} />
          <InfoRow label={_locate("ppp_dip_limite_economico")} value={formatEuro(dip.limiteEconomico)} />
          <InfoRow label={_locate("ppp_dip_data_decisione_avvio")} value={formatDate(dip.dataDecisioneAvvio)} />
          <InfoRow label={_locate("ppp_dip_data_termine_redazione")} value={formatDate(dip.dataTermineRedazione)} />
          {dip.haDocfap && (
            <InfoRow label={_locate("ppp_dip_numero_docfap")} value={dip.numeroDocfap} />
          )}
        </div>
      </div>

      {/* Sezioni tecniche obbligatorie (art. 3 Allegato I.7) */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5 mb-4">
        <h2 className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-4">
          Contenuti obbligatori — Art. 3 Allegato I.7 D.lgs 36/2023
        </h2>
        <div className="space-y-4">
          <InfoRow label={_locate("ppp_dip_stato_dei_luoghi")} value={dip.statoDeiLuoghi} />
          <InfoRow label={_locate("ppp_dip_obiettivi_fabbisogni")} value={dip.obiettiviFabbisogni} />
          <InfoRow label={_locate("ppp_dip_livelli_servizio")} value={dip.livelliServizio} />
          <InfoRow label={_locate("ppp_dip_requisiti_tecnici")} value={dip.requisitiTecnici} />
          <InfoRow label={_locate("ppp_dip_copertura_finanziaria")} value={dip.coberturaFinanziaria} />
        </div>
      </div>

      {/* Approvazione (se approvato) */}
      {dip.stato === "approvato" && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-5 mb-4">
          <h2 className="text-sm font-semibold text-green-700 dark:text-green-400 uppercase tracking-wide mb-4">
            Approvazione dirigenziale
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <InfoRow label={_locate("ppp_dip_numero_atto")} value={dip.numeroAttoApprovazione} />
            <InfoRow label={_locate("ppp_dip_data_approvazione")} value={formatDate(dip.dataApprovazione)} />
            <InfoRow label={_locate("ppp_dip_approvato_da")} value={dip.approvatoDaNome} />
          </div>
        </div>
      )}

      {/* Note */}
      {dip.note && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5 mb-4">
          <InfoRow label={_locate("ppp_dip_note")} value={dip.note} />
        </div>
      )}

      {/* Proposte PPP collegate — verifica coerenza (fase 5-6) */}
      {dip.proposte && dip.proposte.length > 0 && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5 mb-4">
          <h2 className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">
            {_locate("ppp_dip_proposte_collegate")}
          </h2>
          <p className="text-xs text-gray-400 mb-4">{_locate("ppp_dip_proposte_collegate_hint")}</p>
          <div className="space-y-2">
            {dip.proposte.map((p) => {
              const statoCls = STATO_PROPOSTA_CFG[p.stato]?.cls ?? "bg-gray-100 text-gray-600";
              return (
                <div key={p.id} className="border border-gray-100 dark:border-gray-700 rounded-md px-4 py-3">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${statoCls}`}>
                      {_locate(`ppp_proposta_stato_${p.stato}`) ?? p.stato}
                    </span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {p.titolo || _locate("ppp_dip_proposta_senza_titolo")}
                    </span>
                  </div>
                  <div className="text-xs text-gray-400 flex flex-wrap gap-3 mt-0.5">
                    {p.operatoreEconomico && <span>{_locate("ppp_dip_proposta_operatore")}: {p.operatoreEconomico}</span>}
                    {p.dataPresentazione && <span>{_locate("ppp_dip_proposta_presentata")}: {formatDate(p.dataPresentazione)}</span>}
                  </div>
                  {p.descrizione && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 line-clamp-3">{p.descrizione}</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Coerenza con proposta PPP (fase 5-6) */}
      {dip.procedimentoId && dip.proposte && dip.proposte.length > 0 && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5 mb-4">
          <h2 className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">
            Coerenza con proposta PPP
          </h2>
          <p className="text-xs text-gray-400 mb-4">
            Confronto tra i requisiti del DIP e le proposte ricevute. Clicca "Aggiorna confronto" per eseguire la verifica.
          </p>
          <div className="space-y-4">
            {dip.proposte.map((p) => {
              const coerenza = coerenzaMap[p.id];
              const isLoadingThis = coerenzaLoading[p.id];
              const esitoGlobale  = coerenza ? ESITO_CFG[coerenza.esito] ?? ESITO_CFG.non_verificabile : null;
              return (
                <div key={p.id} className="border border-gray-100 dark:border-gray-700 rounded-md p-4">
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <div className="flex items-center gap-2">
                      {esitoGlobale && (
                        <span className={`px-2 py-0.5 rounded text-xs font-semibold ${esitoGlobale.cls}`}>
                          {esitoGlobale.label}
                        </span>
                      )}
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {p.titolo || "Proposta senza titolo"}
                      </span>
                    </div>
                    <button
                      onClick={() => aggiornaCoerenza(p.id)}
                      disabled={isLoadingThis}
                      className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 disabled:opacity-50 transition-colors"
                    >
                      {isLoadingThis ? "Caricamento…" : "Aggiorna confronto"}
                    </button>
                  </div>

                  {coerenza && (
                    <>
                      {coerenza.dettagli.length > 0 && (
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs border-collapse">
                            <thead>
                              <tr className="bg-gray-50 dark:bg-gray-700">
                                <th className="text-left px-3 py-2 font-semibold text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600">Campo</th>
                                <th className="text-left px-3 py-2 font-semibold text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600">Requisito DIP</th>
                                <th className="text-left px-3 py-2 font-semibold text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600">Proposta</th>
                                <th className="text-left px-3 py-2 font-semibold text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600">Esito</th>
                              </tr>
                            </thead>
                            <tbody>
                              {coerenza.dettagli.map((d, i) => (
                                <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                  <td className="px-3 py-2 border border-gray-200 dark:border-gray-600 font-medium text-gray-700 dark:text-gray-300">{d.campo}</td>
                                  <td className="px-3 py-2 border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 max-w-[200px] truncate" title={d.valoreDip ?? "—"}>{d.valoreDip ?? "—"}</td>
                                  <td className="px-3 py-2 border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400">
                                    <span className="inline-flex items-center gap-1">
                                      {d.valoreProposta ?? "—"}
                                      {(d.campo === "Livelli di servizio" || d.campo === "Requisiti tecnici") && d.valoreProposta && (
                                        <span
                                          className="text-gray-400 cursor-help"
                                          title="L'esito riflette il campo 'Coerenza con il DIP approvato' impostato dal RUP nella scheda Valutazione della proposta. Non è un confronto automatico del testo."
                                        >ⓘ</span>
                                      )}
                                    </span>
                                  </td>
                                  <td className="px-3 py-2 border border-gray-200 dark:border-gray-600">
                                    <EsitoBadge esito={d.esito} />
                                    {d.nota && <p className="text-gray-400 mt-0.5">{d.nota}</p>}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {coerenza.campiMancanti.length > 0 && (
                        <p className="text-xs text-amber-600 mt-2">
                          Campi DIP non compilati: {coerenza.campiMancanti.join(", ")}
                        </p>
                      )}

                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 italic">
                        {coerenza.noteAutomatiche}
                      </p>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Modal approvazione (overlay custom) */}
      {showApprova && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {_locate("ppp_dip_approva")}
            </h3>
            <div className="space-y-4">
              <Input
                label={_locate("ppp_dip_numero_atto")}
                required
                value={approvaForm.numeroAttoApprovazione}
                onChange={(e) => setApprovaForm((f) => ({ ...f, numeroAttoApprovazione: e.target.value }))}
                placeholder={_locate("ppp_dip_numero_atto_hint")}
              />
              <Input
                label={_locate("ppp_dip_data_approvazione")}
                type="date"
                required
                value={approvaForm.dataApprovazione}
                onChange={(e) => setApprovaForm((f) => ({ ...f, dataApprovazione: e.target.value }))}
              />
            </div>
            <NotificationsList errors={approvaErrors} />

            <div className="flex gap-2 justify-end mt-5 pt-4 border-t border-gray-100">
              <SimpleButton customFunc={() => setShowApprova(false)} text={_locate("ppp_annulla")} />
              <LoadingButton
                isLoading={saving}
                customFunc={approvaForm.numeroAttoApprovazione && approvaForm.dataApprovazione ? handleApprova : undefined}
                text={_locate("ppp_dip_approva")}
              />
            </div>
          </div>
        </div>
      )}

      </div>{/* fine card */}

      {/* FormDialog modifica DIP */}
      {editForm && (
        <FormDialog title={_locate("ppp_modifica")} size="w-[640px]">
          <div className="space-y-4">
            <Input label={_locate("ppp_dip_oggetto")} required value={editForm.oggetto} onChange={setEdit("oggetto")} />

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {_locate("ppp_dip_procedimento")}
              </label>
              <Select
                value={editForm.procedimentoId}
                onChange={(e) => setEditForm((f) => ({ ...f, procedimentoId: e.target.value }))}
                options={[
                  { value: "", label: _locate("ppp_dip_procedimento_placeholder") },
                  ...(procedimenti ?? []).map((p) => ({ value: p.id, label: p.titolo })),
                ]}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Input label={_locate("ppp_dip_data_decisione_avvio")} type="date" value={editForm.dataDecisioneAvvio} onChange={setEdit("dataDecisioneAvvio")} />
              <Input label={_locate("ppp_dip_data_termine_redazione")} type="date" value={editForm.dataTermineRedazione} onChange={setEdit("dataTermineRedazione")} />
            </div>

            <TextArea label={_locate("ppp_dip_stato_dei_luoghi")} rows={4} value={editForm.statoDeiLuoghi} onChange={setEdit("statoDeiLuoghi")} placeholder={_locate("ppp_dip_stato_dei_luoghi_hint")} />
            <TextArea label={_locate("ppp_dip_obiettivi_fabbisogni")} rows={4} value={editForm.obiettiviFabbisogni} onChange={setEdit("obiettiviFabbisogni")} />
            <TextArea label={_locate("ppp_dip_livelli_servizio")} rows={4} value={editForm.livelliServizio} onChange={setEdit("livelliServizio")} />
            <TextArea label={_locate("ppp_dip_requisiti_tecnici")} rows={4} value={editForm.requisitiTecnici} onChange={setEdit("requisitiTecnici")} />

            <div className="grid grid-cols-2 gap-4">
              <Input label={_locate("ppp_dip_limite_economico")} type="number" min={0} step={1000} value={editForm.limiteEconomico} onChange={setEdit("limiteEconomico")} />
              <TextArea label={_locate("ppp_dip_copertura_finanziaria")} rows={2} value={editForm.coberturaFinanziaria} onChange={setEdit("coberturaFinanziaria")} />
            </div>

            <div className="flex items-center gap-2">
              <input type="checkbox" id="haDocfapEdit" checked={editForm.haDocfap} onChange={setEditCheck("haDocfap")} className="w-4 h-4 text-blue-600 rounded border-gray-300" />
              <label htmlFor="haDocfapEdit" className="text-sm text-gray-700 dark:text-gray-300">{_locate("ppp_dip_ha_docfap")}</label>
            </div>
            {editForm.haDocfap && (
              <Input label={_locate("ppp_dip_numero_docfap")} value={editForm.numeroDocfap} onChange={setEdit("numeroDocfap")} required />
            )}

            <TextArea label={_locate("ppp_dip_note")} rows={2} value={editForm.note} onChange={setEdit("note")} />

            <NotificationsList errors={editErrors} />

            <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
              <SimpleButton customFunc={() => setOpenForm(false)} text={_locate("ppp_annulla")} />
              <LoadingButton isLoading={saving} customFunc={editForm.oggetto ? handleAggiorna : undefined} text={_locate("ppp_dip_salva")} />
            </div>
          </div>
        </FormDialog>
      )}
    </div>
  );
}
